<script setup lang="ts">
import { ref, watch } from 'vue'
import type { Comic } from '@tanko/shared'
import { Button, ErrorMessage, Field, FieldGroup, Fieldset, IconButton, InlineAlert, Input, Label, StateCard, useConfirm } from 'catalyst-vue'
import { Trash2 } from '@lucide/vue'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { errorMessage } from '../../lib/fs-access'
import LibraryOverlay from './library-overlay.vue'
import CategoryPickerField from './category-picker-field.vue'
import { useControlSize, useIconControlSize } from '../../lib/library-layout'

const props = defineProps<{ comic?: Comic }>()
const emit = defineEmits<{ close: [] }>()
const store = useCloudLibraryStore()
const { confirm } = useConfirm()
const controlSize = useControlSize()
const iconSize = useIconControlSize()
const title = ref('')
const author = ref('')
const coverPage = ref(0)
const selected = ref<string[]>([])
const loading = ref(false)
const saving = ref(false)
const error = ref('')
let generation = 0
watch(() => props.comic?.id, async id => {
  const token = ++generation
  error.value = ''
  if (!id) return
  loading.value = true
  try {
    const detail = await store.detail(id)
    if (token !== generation) return
    title.value = detail.title
    author.value = detail.author
    coverPage.value = detail.coverPage
    selected.value = detail.categoryIds
  } catch (cause) { if (token === generation) error.value = errorMessage(cause) }
  finally { if (token === generation) loading.value = false }
}, { immediate: true })
async function save() {
  if (!props.comic || saving.value) return
  saving.value = true; error.value = ''
  try {
    await store.saveComic(props.comic.id, { title: title.value, author: author.value, coverPage: coverPage.value, categoryIds: selected.value })
    emit('close')
  } catch (cause) { error.value = errorMessage(cause) }
  finally { saving.value = false }
}
async function remove() {
  const comic = props.comic
  if (!comic || saving.value) return
  const removed = await confirm({
    title: `刪除「${comic.title}」的雲端副本？`,
    description: '作品會立即無法從雲端閱讀；電腦裡的本地作品與原始檔案不會刪除。',
    tone: 'danger', confirmLabel: '刪除雲端副本', pendingLabel: '正在刪除…', errorMessage,
    onConfirm: () => store.deleteComic(comic.id),
  })
  if (removed) emit('close')
}
function close() { if (!saving.value) emit('close') }
</script>

<template>
  <LibraryOverlay :open="!!comic" title="整理雲端作品" description="修改私人雲端的書名與分類；本地 metadata 不會被覆蓋。" :pending="saving" @close="close">
    <StateCard v-if="loading" title="正在讀取雲端資料" message="取得作品分類與封面設定…" :actionable="false" />
    <form v-else id="cloud-metadata-form" @submit.prevent="save">
      <Fieldset :disabled="saving || !!error">
        <FieldGroup>
          <Field><Label>書名</Label><Input v-model="title" required :invalid="!title.trim()" /><ErrorMessage v-if="!title.trim()">請填寫書名，才能儲存作品。</ErrorMessage></Field>
          <Field><Label>作者</Label><Input v-model="author" placeholder="尚未填寫" /></Field>
          <CategoryPickerField v-model="selected" :categories="store.categories" :disabled="saving" legend="放進哪些雲端分類" />
        </FieldGroup>
      </Fieldset>
      <InlineAlert v-if="error" tone="error" class="mt-4">{{ error }}</InlineAlert>
    </form>
    <template #actions>
      <IconButton color="danger" :size="iconSize" class="overlay-destructive" :disabled="saving || loading" :label="`刪除「${comic?.title ?? ''}」的雲端副本`" @click="remove"><Trash2 :size="17" aria-hidden="true" /></IconButton>
      <Button outline :size="controlSize" :disabled="saving" @click="close">取消</Button>
      <Button color="primary" :size="controlSize" type="submit" form="cloud-metadata-form" :disabled="loading || saving || !!error || !title.trim()">{{ saving ? '正在儲存…' : '儲存' }}</Button>
    </template>
  </LibraryOverlay>
</template>
